import { useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import { serverStore } from '../../../stores/server.store';

const PRESETS = [2, 4, 6, 8, 12, 16];
// Java belegt spürbar mehr als eingestellt (ATM 10: 12 GB eingestellt, 13,8 GB belegt).
const JVM_OVERHEAD = 1.15;

const gb = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 1 });

interface Budget {
  totalGb: number;
  availableGb: number;
  others: { name: string | null; limitGb: number; usedGb: number }[];
}

/** RAM-Regler mit Schnellwahl und einer Leiste, wie voll der Host mit den anderen laufenden Servern wird. */
export const RamField = ({ serverId, value, saved, running, onChange }: {
  serverId: string;
  value: number;
  saved: number;
  running: boolean;
  onChange: (v: number) => void;
}) => {
  const [budget, setBudget] = useState<Budget | null>(null);

  useEffect(() => {
    let alive = true;
    serverStore.fetchRamBudget(serverId).then((b) => { if (alive) setBudget(b); });
    return () => { alive = false; };
  }, [serverId, running]);

  const total = budget ? Math.max(1, Math.floor(budget.totalGb)) : 32;
  const max = Math.max(value, total);
  const presets = PRESETS.filter((p) => p <= total);
  const others = budget?.others || [];
  const othersGb = others.reduce((a, o) => a + (o.usedGb || o.limitGb || 0), 0);
  const needGb = value * JVM_OVERHEAD;
  const tight = budget ? budget.totalGb - othersGb - needGb < 1 : false;
  const pct = (n: number) => `${Math.max(0, Math.min(100, (n / (budget?.totalGb || total)) * 100))}%`;
  const names = others.filter((o) => o.name).map((o) => `${o.name} ${gb(o.usedGb || o.limitGb)} GB`);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-fg-muted font-medium ml-0.5">Arbeitsspeicher (RAM)</div>
          <div className="font-mono text-3xl text-fg tracking-tight mt-1 tabular-nums">
            {value}<span className="text-lg text-fg-muted"> GB</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 justify-end">
          {presets.map((p) => (
            <button
              type="button"
              key={p}
              onClick={() => onChange(p)}
              className={cn(
                'px-2.5 py-1 rounded-md text-xs font-mono border transition-colors',
                value === p ? 'bg-ember/15 border-ember/40 text-ember' : 'border-line text-fg-muted hover:text-fg hover:border-line-strong',
              )}
            >
              {p} GB
            </button>
          ))}
        </div>
      </div>

      <input
        type="range"
        min={1}
        max={max}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        aria-label="Arbeitsspeicher in GB"
        className="w-full h-2 cursor-pointer accent-[var(--color-ember)]"
      />

      {budget && (
        <div className="space-y-2">
          <div className="h-2.5 rounded-full bg-white/[0.06] overflow-hidden flex" title="Arbeitsspeicher des Hosts">
            <div className="h-full bg-white/25" style={{ width: pct(othersGb) }} />
            <div className={cn('h-full transition-all', tight ? 'bg-warn' : 'bg-ember')} style={{ width: pct(needGb) }} />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-fg-muted">
            <span className="inline-flex items-center gap-1.5">
              <i className="w-2 h-2 rounded-sm bg-white/25" />
              Andere laufende Server: {gb(othersGb)} GB{names.length ? ` (${names.join(', ')})` : ''}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className={cn('w-2 h-2 rounded-sm', tight ? 'bg-warn' : 'bg-ember')} />
              Dieser Server: ca. {gb(needGb)} GB
            </span>
            <span>Host gesamt: {gb(budget.totalGb)} GB</span>
          </div>
          {tight && (
            <p className="text-xs text-warn leading-relaxed">
              Zusammen mit den anderen laufenden Servern wird der Arbeitsspeicher knapp – dann stürzen Server ab.
              Lieber weniger RAM geben oder vorher einen anderen Server stoppen.
            </p>
          )}
        </div>
      )}

      {running && value !== saved && <p className="text-xs text-fg-muted">Gilt ab dem nächsten Neustart.</p>}
    </div>
  );
};
